import type { FC, FormEvent } from 'react';
import { UserSearch, Loader2, Sparkles } from 'lucide-react';

const PERSONAS = [
  { value: '', label: 'Détection auto' },
  { value: 'cto', label: 'CTO' },
  { value: 'product_owner', label: 'Product Owner' },
  { value: 'ceo_startup', label: 'CEO Startup' },
  { value: 'qa_lead', label: 'QA Lead' },
  { value: 'vp_product', label: 'VP Product' },
] as const;

const inputClasses =
  'w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 outline-none transition-colors';

const selectClasses =
  'w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2.5 text-sm text-slate-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 outline-none transition-colors';

const textareaClasses =
  'w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 outline-none resize-none transition-colors';

interface ProspectEnrichmentPanelProps {
  profileText: string;
  knownPersona: string;
  companyName: string;
  sector: string;
  isLoading: boolean;
  onProfileTextChange: (value: string) => void;
  onKnownPersonaChange: (value: string) => void;
  onCompanyNameChange: (value: string) => void;
  onSectorChange: (value: string) => void;
  onSubmit: (e: FormEvent) => void;
}

const ProspectEnrichmentPanel: FC<ProspectEnrichmentPanelProps> = ({
  profileText,
  knownPersona,
  companyName,
  sector,
  isLoading,
  onProfileTextChange,
  onKnownPersonaChange,
  onCompanyNameChange,
  onSectorChange,
  onSubmit,
}) => {
  const isValid = profileText.trim().length >= 20;

  return (
    <div className="fc-card p-5">
      <div className="flex items-center gap-2 mb-4">
        <UserSearch className="w-4 h-4 text-blue-600 dark:text-blue-400" />
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Prospect Profiler</h3>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">
            Profil du prospect <span className="text-red-500">*</span>
          </label>
          <textarea
            value={profileText}
            onChange={(e) => onProfileTextChange(e.target.value)}
            placeholder="Collez ici le profil LinkedIn, la bio du site web, ou toute information sur le prospect (min. 20 caractères)..."
            rows={5}
            className={textareaClasses}
          />
          {profileText.length > 0 && profileText.trim().length < 20 && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">Minimum 20 caractères requis</p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">Persona connu</label>
            <select value={knownPersona} onChange={(e) => onKnownPersonaChange(e.target.value)} className={selectClasses}>
              {PERSONAS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">Entreprise</label>
            <input type="text" value={companyName} onChange={(e) => onCompanyNameChange(e.target.value)} placeholder="Ex: Stripe" className={inputClasses} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">Secteur</label>
            <input type="text" value={sector} onChange={(e) => onSectorChange(e.target.value)} placeholder="Ex: FinTech" className={inputClasses} />
          </div>
        </div>

        <div className="flex justify-end pt-1">
          <button
            type="submit"
            disabled={!isValid || isLoading}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {isLoading ? 'Analyse...' : 'Analyser le prospect'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProspectEnrichmentPanel;
